import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantLogo } from '../../contexts/TenantBrandingContext';
import { api } from '../../lib/api';

export const CheckoutSuccessPage: React.FC = () => {
  const { logoUrl, logoAlt } = useTenantLogo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingInfo, setBillingInfo] = useState<{ plan?: string } | null>(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Clear redirect flags on checkout success
    sessionStorage.removeItem('incomplete_billing_redirect_attempted');
    sessionStorage.removeItem('redirecting_to_checkout');
    sessionStorage.setItem('from_checkout_success', 'true');
    localStorage.removeItem('redirect_to_checkout');
    localStorage.removeItem('redirect_to_checkout_after_attestation');
    localStorage.removeItem('redirect_to_checkout_after_login');
    localStorage.removeItem('checkout_plan');
    localStorage.removeItem('checkout_plan_after_login');
    localStorage.removeItem('pending_registration');
    
    // Invalidate billing info cache to force refresh
    queryClient.invalidateQueries({ queryKey: ['billing-info'] });
    
    // Fetch billing info to get the plan
    const fetchBillingInfo = async () => {
      try {
        const response = await api.billing.getInfo();
        setBillingInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch billing info:', error);
        // Don't block the success page if billing fetch fails
      }
    };
    
    // Fetch billing info after a short delay to allow webhook to process
    const billingTimer = setTimeout(() => {
      fetchBillingInfo();
    }, 1000);
    
    // Checkout was successful - wait a moment for webhook to process
    // Then redirect to dashboard
    const timer = setTimeout(() => {
      setIsLoading(false);
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }, 2000);

    return () => {
      clearTimeout(timer);
      clearTimeout(billingTimer);
    };
  }, [navigate, queryClient]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {logoUrl && (
            <img
              className="mx-auto h-12 w-auto"
              src={logoUrl}
              alt={logoAlt}
            />
          )}
          
          {isLoading ? (
            <>
              <div className="mt-6 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Processing Payment...
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we confirm your subscription.
              </p>
            </>
          ) : error ? (
            <>
              <div className="mt-6 flex justify-center">
                <div className="rounded-full bg-red-100 p-3">
                  <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Payment Processing Error
              </h2>
              <p className="mt-2 text-sm text-red-600">
                {error}
              </p>
              <div className="mt-6">
                <button
                  onClick={() => navigate('/billing')}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Go to Billing
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mt-6 flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Payment Successful!
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Your <span className="capitalize font-semibold">{billingInfo?.plan || 'starter'}</span> plan subscription has been activated. Your free trial has started!
              </p>
              <p className="mt-4 text-sm text-gray-500">
                Redirecting to dashboard...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
